import Add from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import {
  Alert,
  AlertProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  SxProps,
  TextField,
  Typography,
} from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import Modal from '@mui/material/Modal';
import {
  DataGrid,
  GridActionsCellItem,
  GridColumns,
  GridEditInputCell,
  GridEventListener,
  GridFooterContainer,
  GridPagination,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
  GridValueGetterParams,
  GridValueSetterParams,
  MuiEvent,
  useGridApiContext,
} from '@mui/x-data-grid';
import { isEmpty, isNil, sortBy } from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import InputMask from 'react-input-mask';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { spreadSxProp } from 'src/utils/cssStyles';
import * as Yup from 'yup';
import {
  Identifier,
  ValueSetComposeIncludeConcept,
} from 'src/careflow/ph-core/fhir-client/resources';
import DialogCloseButton from 'src/components/DialogCloseButton';

interface IdentifierDetailsProps {
  catalog: ValueSetComposeIncludeConcept[];
  identifiers: Identifier[];
  hiddenIdentifiers?: Identifier[];
  onUpdateIdentifiers: (newIdentifiers: Identifier[]) => void;
  permissions?: {
    isAllowedToAdd: boolean;
    isAllowedToEdit: boolean;
    isAllowedToDelete: boolean;
    isAllowedToViewHidden: boolean;
    isAllowedToEditHidden: boolean;
    isAllowedToDeleteHidden: boolean;
    isAllowedToEditExternal?: boolean;
    isAllowedToDeleteExternal?: boolean;
  };
  sxContainer?: SxProps;
  systemVS?: string;
}

export function IdentifierDetails({
  catalog,
  identifiers,
  onUpdateIdentifiers,
  sxContainer,
  permissions,
  hiddenIdentifiers,
  systemVS,
}: IdentifierDetailsProps) {
  const { i18n } = useLocales();
  const { componentsData, configurations } = useTenantConfigData();
  const { showMedicaID } = componentsData ?? {};

  const [disabledadd, setDisabledAdd] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [save, setSave] = React.useState(false);
  const [idDelete, setIdDelete] = React.useState('');

  const [newrow, setNewRow] = React.useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = React.useState(false);
  const [newRowModel, setNewRowModel] = React.useState<GridRowModel | null>(null);
  const [shouldPromptDuplicateWarning, setShouldPromptDuplicateWarning] = React.useState(false);
  const [switchState, setSwitchState] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [snackbar, setSnackbar] = useState<Pick<AlertProps, 'children' | 'severity'> | null>(null);

  const handleDuplicateDialogClose = () => {
    setIsDuplicateDialogOpen(false);
  };

  const saveDeleteIdentifier = () => {
    const rowToDelete = rows.find((row: any) => row.type === idDelete);
    if (rowToDelete && isLockedIdentifier(rowToDelete)) {
      setSnackbar({
        children: 'Cannot delete internal identifier',
        severity: 'error',
      });
      handleClose();
      return;
    }

    if (rowToDelete && isExternalIdentifier(rowToDelete)) {
      if (!permissions?.isAllowedToDeleteExternal) {
        setSnackbar({
          children: 'You do not have permission to delete external identifiers',
          severity: 'error',
        });
        handleClose();
        return;
      }
    }

    setRows(rows.filter((row: any) => row.type != idDelete));
    handleClose();
    setSave(true);
  };
  const saveId = (id: any) => {
    const typeModal = rows.filter((row: any) => row.id === id)[0].type;
    setIdDelete(typeModal);
    handleOpen();
  };

  const FilterCat = (cat: any) => {
    for (let i = 0; i < rows.length; i++) {
      const filter = rows[i].type;

      if (cat.includes(filter)) {
        cat = cat.filter((cas: any) => cas != filter);
      }
    }
    return cat;
  };

  const stylemodal = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'auto',
    bgcolor: 'background.paper',
    borderRadius: '16px',
    boxShadow: '0 0 2px 0 rgb(145 158 171 / 20%), 0 12px 24px -4px rgb(145 158 171 / 12%)',
    p: 4,
  };

  const FilterIdent = (identifiers: any) => {
    const identifiersList = identifiers.filter(
      (identifier: any) => !hiddenIdentifiers?.includes?.(identifier)
    );
    const filteredIdentifiers = identifiersList
      ?.map((identifier: any, index: any) => {
        const ident = { ...identifier };
        if (!ident?.system) return null;
        const code = ident.type?.coding?.[0]?.code;

        const catalogMatch = catalog?.find((word: any) => word.code === ident.system);
        if (catalogMatch) {
          ident.type = catalogMatch.display;
          if (code) ident.code = code;
        } else if (typeof ident.type == 'object' && ident.type != ident.text && code) {
          ident.text = ident.type?.text;
          ident.type = ident.type?.text;
          if (code) ident.code = code;
        } else {
          if (ident.type != ident.text) {
            if (ident.type?.text) {
              ident.text = ident.type?.text;
              ident.type = ident.type?.text;
              ident.hide = true;
            } else {
              ident.type = ident.system;
              ident.hide = true;
            }
          } else {
            if (!ident.hasOwnProperty('type')) {
              ident.type = ident.system;
              ident.hide = true;
            }
          }
        }
        ident.id = `${ident.system}${ident.value}`;
        return ident;
      })
      .filter((identifier: any) => !!identifier);
    if (identifiers == undefined) {
      return [];
    }
    return sortBy(filteredIdentifiers, [(item) => (item.hide ? 1 : 0)]);
  };

  const [rows, setRows] = React.useState(FilterIdent(identifiers));
  const [editmoderow, setEditModerow] = React.useState(false);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
  const handleRowEditStart = (params: GridRowParams, event: MuiEvent<React.SyntheticEvent>) => {
    event.defaultMuiPrevented = true;
  };
  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    event.defaultMuiPrevented = true;
  };
  const handleSaveClick =
    (id: GridRowId, shouldPromptDuplicateWarning = true) =>
    () => {
      setSnackbar(null);
      setShouldPromptDuplicateWarning(shouldPromptDuplicateWarning);
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

  const handleCancelClick = (id: GridRowId) => () => {
    setSnackbar(null);
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row: any) => row.id === id);
    if (editedRow!.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }

    setDisabledAdd(false);
    setNewRow(false);
  };

  const handleEditClick = (id: GridRowId) => () => {
    setDisabledAdd(true);
    setEditModerow(true);
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const schema = Yup.object({
    type: Yup.string().required('Type is required'),
    value: Yup.string()
      .required('Value is required')
      .when('type', {
        is: 'Medicaid ID',
        then: Yup.string().matches(/^\d{9}$/, 'Must contain exactly 9 numbers'),
        otherwise: Yup.string().when('type', {
          is: 'Social Security Number(SSN)',
          then: Yup.string().matches(/^\d{9}$/, 'Must contain exactly 9 numbers'),
        }),
      }),
    end: Yup.date()
      .nullable()
      .transform((curr, orig) => (moment(orig).isValid() ? curr : null))
      .min(Yup.ref('start'), "Start Date can't be greater than End Date"),
  });

  const saveChanges = async () => {
    try {
      const identifiernews = rows?.map((list: any) => {
        let setPeriod: any = {};

        if (list?.start && moment(list.start).isValid()) {
          setPeriod.start = list.start.toISOString();
        } else if (!isNil(list?.period?.start)) {
          setPeriod.start = list.period.start;
        }

        if (list?.end && moment(list.end).isValid()) {
          setPeriod.end = list.end.toISOString();
        } else if (!isNil(list?.period?.end)) {
          setPeriod.end = list.period.end;
        }

        const setIdentifierType =
          identifiers?.find((e) => `${e.system}${e.value}` === list?.id) ?? null;
        let identifiernew = {};

        if (!list?.isNew && setIdentifierType) {
          const isLocked = isLockedIdentifier(list);
          if (isLocked && setIdentifierType.value !== list?.value) {
            identifiernew = {
              ...setIdentifierType,
              ...(!isEmpty(setPeriod) && { period: setPeriod }),
            };
          } else if (
            setIdentifierType?.type?.text === list?.type ||
            setIdentifierType?.system === list?.system
          ) {
            identifiernew = {
              ...setIdentifierType,
              value: list?.value,
              ...(!isEmpty(setPeriod) && { period: setPeriod }),
            };
          }
        } else {
          const getIdentifierType = catalog?.map(({ code, display, extension }) => {
            const valueUri = extension?.[0]?.extension?.[0]?.valueUri || extension?.[0]?.valueUri;
            let systemUri = valueUri || configurations?.fhir?.baseUrl;
            let _code = code;

            if (code.includes('|')) {
              const codeSplit = code.split('|');
              _code = codeSplit[1];
              systemUri = codeSplit[0];
            }

            return {
              system: systemUri,
              type: {
                coding: [{ _code, system: systemVS }],
                ...(display && { text: display }),
              },
            };
          });

          const identifierType = getIdentifierType?.find((e) => e?.type?.text === list?.type);

          identifiernew = {
            value: list?.value || '',
            ...(!isEmpty(setPeriod) && { period: setPeriod }),
            ...identifierType,
          };
        }

        Object.keys(identifiernew).forEach((key) => {
          if (identifiernew[key] === '') delete identifiernew[key];
        });

        return identifiernew;
      });
      onUpdateIdentifiers(identifiernews);
      setNewRow(false);
    } catch {
      console.log('error');
    }
  };

  useEffect(() => {
    if (save == true) {
      saveChanges();
      setSave(false);
    }
  }, [save]);

  useEffect(() => {
    if (newrow == false) {
      setRows(FilterIdent(identifiers));
    }
  }, [catalog, identifiers]);

  const showEdit = permissions?.isAllowedToEdit || permissions?.isAllowedToDelete;

  const isInternalMCCMRN = (row: any): boolean => {
    if (!configurations?.mpi?.systemMrn) return false;
    return row.system === configurations.mpi.systemMrn;
  };

  const isInternalPractitionerIdentifier = (row: any): boolean => {
    if (!catalog || !row?.type) return false;

    const catalogItem = catalog.find((item: any) => item.display === row.type);
    if (!catalogItem) return false;

    const practitionerSystemFromFhir =
      configurations?.fhir?.fhir_practitioner_number_identifier_system;
    const practitionerSystemFromProvider =
      configurations?.provider?.practitionerInternalNumberIdentifierSystem;
    const practitionerSystem = practitionerSystemFromFhir || practitionerSystemFromProvider;

    if (!practitionerSystem) return false;

    return catalogItem.code === practitionerSystem || row.system === practitionerSystem;
  };

  const isLockedIdentifier = (row: any): boolean => {
    return isInternalMCCMRN(row) || isInternalPractitionerIdentifier(row);
  };

  const isExternalIdentifier = (row: any): boolean => {
    if (isLockedIdentifier(row)) {
      return false;
    }

    const internalMrnSystem = configurations?.mpi?.systemMrn;
    if (!internalMrnSystem) {
      return true;
    }

    if (row?.type && catalog) {
      const catalogItem = catalog.find((item: any) => item.display === row.type);

      if (catalogItem) {
        const valueUri = catalogItem.extension?.[0]?.valueUri;

        if (valueUri) {
          if (valueUri === internalMrnSystem) {
            return false;
          }
          return true;
        }

        if (row?.system && row.system === internalMrnSystem) {
          return false;
        }

        return true;
      }
    }

    if (row?.system && catalog) {
      const catalogItem = catalog.find((item: any) => {
        const valueUri = item.extension?.[0]?.valueUri;
        return valueUri && row.system === valueUri;
      });

      if (catalogItem) {
        const valueUri = catalogItem.extension?.[0]?.valueUri;
        if (valueUri === internalMrnSystem) {
          return false;
        }
        return true;
      }

      if (row.system === internalMrnSystem) {
        return false;
      }
    }

    if (row.code && catalog) {
      const catalogItem = catalog.find((item: any) => item.code === row.code);
      if (catalogItem) {
        const valueUri = catalogItem.extension?.[0]?.valueUri;
        if (valueUri) {
          if (valueUri === internalMrnSystem) {
            return false;
          }
          return true;
        }
        if (row?.system && row.system === internalMrnSystem) {
          return false;
        }
        return true;
      }
    }

    const practitionerSystemFromFhir =
      configurations?.fhir?.fhir_practitioner_number_identifier_system;
    const practitionerSystemFromProvider =
      configurations?.provider?.practitionerInternalNumberIdentifierSystem;
    const practitionerSystem = practitionerSystemFromFhir || practitionerSystemFromProvider;

    if (row?.system) {
      if (row.system === internalMrnSystem) {
        return false;
      }
      if (practitionerSystem && row.system === practitionerSystem) {
        return false;
      }
      const hasExternalValueUri = catalog?.some((item: any) => {
        const valueUri = item.extension?.[0]?.valueUri;
        return valueUri && valueUri === row.system && valueUri !== internalMrnSystem;
      });
      if (hasExternalValueUri) {
        return true;
      }
    }

    return true;
  };

  const isExternalIdentifierByType = (typeDisplay: string): boolean => {
    if (!catalog) return false;

    const catalogItem = catalog.find((item: any) => item.display === typeDisplay);
    if (!catalogItem) return false;

    const internalMrnSystem = configurations?.mpi?.systemMrn;
    if (!internalMrnSystem) {
      return true;
    }

    const valueUri = catalogItem.extension?.[0]?.valueUri;

    if (valueUri) {
      if (valueUri === internalMrnSystem) {
        return false;
      }
      return true;
    }

    return true;
  };

  function CustomEditComponent(props: GridRenderEditCellParams) {
    const { id, value, field, hasFocus, row } = props;
    const apiRef = useGridApiContext();

    const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      apiRef.current.setEditCellValue({ id, field, value: newValue });
    };

    const maskTypeMapping: any = {
      ...(showMedicaID && { 'Medicaid ID': '*********' }),
      'Social Security number': '*********',
    };

    let maskType: any = null;

    if (row.type in maskTypeMapping) {
      maskType = maskTypeMapping[row.type];
    }

    if (!maskType) {
      return <GridEditInputCell {...props} />;
    } else {
      return (
        <InputMask mask={maskType} onChange={handleValueChange} value={value}>
          {(inputProps: any) => (
            <TextField
              fullWidth
              {...inputProps}
              onChange={inputProps.onChange}
              InputProps={{
                ...inputProps.InputProps,
              }}
            />
          )}
        </InputMask>
      );
    }
  }

  const columns: GridColumns = [
    {
      field: 'type',
      headerName: `${i18n('patients.details.identifierDetails.type', 'crs')} `,
      type: 'singleSelect',
      valueOptions:
        newrow == false
          ? rows?.map((dis: any) => dis.type)
          : FilterCat(catalog?.map((dis: any) => dis?.display)),
      editable: true,
      minWidth: 150,
      flex: 1,
    },
    {
      field: 'value',
      headerName: `${i18n('patients.details.identifierDetails.number', 'crs')}`,
      type: 'string',
      minWidth: 100,
      editable: true,
      flex: 2,
      renderEditCell: CustomEditComponent,
    },
    {
      field: 'start',
      headerName: `${i18n('patients.details.identifierDetails.start', 'crs')}`,
      editable: true,
      type: 'date',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const start = params.row.period && params.row.period.start;
        const startDate = moment.utc(start ? new Date(start) : null);
        return (
          <Typography variant="body2">
            {!startDate.isValid() ? '' : startDate.format('MM/DD/YYYY')}
          </Typography>
        );
      },
      valueGetter: (params: GridValueGetterParams) => {
        const start = params.row.period ? new Date(params.row.period.start) : null;
        return start;
      },
      valueSetter: (params: GridValueSetterParams) => {
        const start = params.value ?? null;
        return { ...params.row, start };
      },
    },
    {
      field: 'end',
      headerName: `${i18n('patients.details.identifierDetails.end', 'crs')}`,
      editable: true,
      type: 'date',
      flex: 1,

      renderCell: (params: GridRenderCellParams) => {
        const end = params.row.period && params.row.period.end;
        const endDate = moment.utc(end ? new Date(end) : null);
        return (
          <Typography variant="body2">
            {!endDate.isValid() ? '' : endDate.format('MM/DD/YYYY')}
          </Typography>
        );
      },
      valueGetter: (params: GridValueGetterParams) => {
        const end = params.row.period ? new Date(params.row.period.end) : null;
        return end;
      },
      valueSetter: (params: GridValueSetterParams) => {
        const end = params.value ?? null;
        return { ...params.row, end };
      },
    },
    {
      field: 'edit',
      headerName: '',
      type: 'actions',
      cellClassName: 'actions',
      hide: !showEdit,
      flex: 1,
      getActions: ({ id, row }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key={'grid-a-save-' + id}
              icon={<SaveIcon />}
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key={'grid-a-cancel-' + id}
              icon={<CancelIcon />}
              label="Cancel"
              onClick={handleCancelClick(id)}
            />,
          ];
        }

        const isLocked = isLockedIdentifier(row);
        const isExternal = isExternalIdentifier(row);

        let editRow: boolean;
        let deleteRow: boolean;

        if (isLocked) {
          editRow = false;
          deleteRow = false;
        } else if (isExternal) {
          editRow = row?.hide
            ? permissions?.isAllowedToEditHidden ?? false
            : permissions?.isAllowedToEditExternal ?? false;
          deleteRow = row?.hide
            ? permissions?.isAllowedToDeleteHidden ?? false
            : permissions?.isAllowedToDeleteExternal ?? false;
        } else {
          editRow = row?.hide
            ? permissions?.isAllowedToEditHidden ?? false
            : permissions?.isAllowedToEdit ?? false;
          deleteRow = row?.hide
            ? permissions?.isAllowedToDeleteHidden ?? false
            : permissions?.isAllowedToDelete ?? false;
        }

        return [
          editRow ? (
            <GridActionsCellItem
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(id)}
              color="inherit"
              showInMenu
              disabled={disabledadd}
            />
          ) : (
            <></>
          ),
          deleteRow ? (
            <GridActionsCellItem
              disabled={disabledadd}
              label="Delete"
              className="textPrimary"
              onClick={() => {
                saveId(id);
              }}
              color="inherit"
              showInMenu
            />
          ) : (
            <></>
          ),
        ];
      },
    },
  ];

  const handleProcessRowUpdate = async (newRow: GridRowModel) => {
    setSnackbar(null);

    if (!newRow.isNew) {
      const existingRow = rows.find((row: any) => row.id === newRow.id);
      if (existingRow) {
        if (isLockedIdentifier(existingRow)) {
          setSnackbar({
            children: 'Cannot edit internal identifier',
            severity: 'error',
          });
          throw new Error('Cannot edit locked identifier');
        }

        const isExternal = isExternalIdentifier(existingRow);
        if (isExternal && !permissions?.isAllowedToEditExternal) {
          setSnackbar({
            children: 'You do not have permission to edit external identifiers',
            severity: 'error',
          });
          throw new Error('Permission denied');
        }
      }
    }

    const validateFields = await schema.validate(newRow, { abortEarly: false }).catch((err) => {
      return err.errors;
    });

    if (validateFields?.length > 0) throw new Error(validateFields);

    if (
      rows.find(
        (row: any) =>
          (row.value === newRow.value || row.type === newRow.type) && row.id !== newRow.id
      ) &&
      shouldPromptDuplicateWarning
    ) {
      setNewRowModel(newRow);
      setIsDuplicateDialogOpen(true);
      throw new Error('Error');
    } else {
      setDisabledAdd(false);
      const updatedRow = await processRowUpdate(newRow);
      return updatedRow;
    }
  };

  const handleProcessRowUpdateError = React.useCallback((error: Error) => {
    setSnackbar({ children: error.message, severity: 'error' });
  }, []);

  const processRowUpdate = async (newRow: GridRowModel) => {
    if (editmoderow == true) {
      const editRow = rows?.filter((word: any) => word.type === newRow.type)[0];
      newRow.id = `${editRow.system}${editRow.value}`;
      if (typeof newRow.text == 'string' && newRow.text != newRow.type) {
        delete newRow.text;
      }
      setEditModerow(false);
    }
    if (catalog?.filter((dis: any) => newRow.type == dis.display).length > 0) {
      newRow.system = catalog?.filter((dis: any) => newRow.type == dis.display)[0].code;
    }

    const updatedRow = { ...newRow };
    setRows(rows.map((row: any) => (row.id === newRow.id ? updatedRow : row)));
    setSave(true);
    return updatedRow;
  };

  const AddRow = () => {
    setSnackbar(null);
    setDisabledAdd(true);
    const id = rows.length + 1;
    setNewRow(true);
    setRows((oldRows: any) => [{ id, type: '', value: '', isNew: true }, ...oldRows]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'value' },
    }));
  };

  const result = useMemo(() => {
    if (!rows || rows.length === 0) return 0;

    const count = switchState ? rows.length : rows.filter((e) => !e?.hide).length;

    return count > 0 ? count : 0;
  }, [rows, switchState]);

  return (
    <>
      <Stack direction="column" sx={{ padding: 3, pt: 0 }}>
        <Box sx={{ position: 'absolute', top: '20px', right: '25px' }}>
          {permissions?.isAllowedToAdd && (
            <Button
              variant="contained"
              disabled={disabledadd}
              color="primary"
              startIcon={<Add />}
              onClick={AddRow}
            >
              {i18n('patients.details.identifierDetails.button', 'crs')}
            </Button>
          )}
        </Box>

        <Stack spacing={2} sx={{ width: '100%' }}>
          <Box sx={[{ height: 400, width: '100%' }, ...spreadSxProp(sxContainer)]}>
            <DataGrid
              autoPageSize
              rows={rows}
              columns={columns}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
              onRowEditStart={handleRowEditStart}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={handleProcessRowUpdate}
              componentsProps={{
                toolbar: { setRows, setRowModesModel },
              }}
              getRowClassName={(params) => (params.row.hide && !switchState ? 'hiddenRow' : '')}
              sx={{
                '& .hiddenRow': { display: 'none' },
                '& .MuiDataGrid-row': {
                  borderBottom: '1px solid #EAECF0',
                  '&:nth-of-type(odd)': {
                    backgroundColor: '#FAFAFA',
                  },
                  '&:hover': {
                    backgroundColor: '#F5F6F7',
                  },
                },
              }}
              components={{
                Footer: () => (
                  <GridFooterContainer>
                    {permissions?.isAllowedToViewHidden && rows?.length > 0 && (
                      <FormControlLabel
                        control={
                          <Switch
                            disabled={rows?.length === 0}
                            checked={switchState}
                            onChange={(event) => {
                              const newOrder = event.target.checked ? true : false;
                              setSwitchState(newOrder);
                            }}
                          />
                        }
                        label="Show hidden"
                      />
                    )}
                    <GridPagination
                      count={result}
                      sx={{
                        marginLeft: 'auto',
                      }}
                    />
                  </GridFooterContainer>
                ),
              }}
              experimentalFeatures={{ newEditingApi: true }}
              onProcessRowUpdateError={handleProcessRowUpdateError}
            />
          </Box>
          {!!snackbar && <Alert {...snackbar} onClose={() => setSnackbar(null)} />}
        </Stack>

        <Modal
          aria-labelledby="transition-modal-title"
          aria-describedby="transition-modal-description"
          open={open}
          onClose={handleClose}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
          }}
        >
          <Fade in={open}>
            <Box sx={stylemodal}>
              <DialogCloseButton onClose={handleClose} />
              <Typography id="transition-modal-title" variant="h6" component="h2">
                <div style={{ textAlign: 'center', fontSize: '25px' }}>
                  Delete {idDelete} type identifier
                </div>
              </Typography>
              <div style={{ textAlign: 'center', paddingTop: '30px' }}>
                <Button variant="contained" onClick={saveDeleteIdentifier}>
                  Confirm
                </Button>
              </div>
            </Box>
          </Fade>
        </Modal>
      </Stack>
      <Dialog
        open={isDuplicateDialogOpen}
        onClose={handleDuplicateDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogCloseButton onClose={handleDuplicateDialogClose} />
        <DialogTitle id="alert-dialog-title">{'Potential Duplicated Identifier'}</DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <DialogContentText id="alert-dialog-description">
            There is an existing identifier with some of the values
            <br />
            <strong>{newRowModel?.value}</strong> or <strong>{newRowModel?.type}</strong>.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDuplicateDialogClose} autoFocus>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
